import { cn } from '@goaltree/ui/lib/utils'

const bar = 'animate-pulse rounded bg-muted'

// Loading placeholders shaped like the Today / Everyday rows they stand in for.

export function SkeletonTaskRow({ title, meta = 40 }: { title: number; meta?: number }) {
  return (
    <div className="border-b border-border/50 py-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 size-5 shrink-0 animate-pulse rounded-full bg-muted" />
        <div className="min-w-0 flex-1">
          <div className={cn(bar, 'h-3.5')} style={{ width: `${title}%` }} />
          <div className="mt-2 flex items-center gap-2">
            <div className={cn(bar, 'h-2.5 w-14')} />
            <div className={cn(bar, 'h-2.5')} style={{ width: `${meta}%` }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function SkeletonHabitRow({ title, desc = 70 }: { title: number; desc?: number }) {
  return (
    <div className="border-b border-border/50 py-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 size-4 shrink-0 animate-pulse rounded-md bg-muted" />
        <div className="min-w-0 flex-1">
          <div className={cn(bar, 'h-3.5')} style={{ width: `${title}%` }} />
          <div className={cn(bar, 'mt-2 h-2.5 bg-muted/60')} style={{ width: `${desc}%` }} />
          <div className="mt-2 flex items-center gap-2">
            <div className={cn(bar, 'h-2.5 w-12')} />
            <div className={cn(bar, 'h-2.5 w-10')} />
          </div>
          <div className="mt-2.5 flex items-center gap-[3px]">
            {Array.from({ length: 14 }, (_, i) => (
              <div key={i} className="size-[15px] animate-pulse rounded-[4px] bg-muted/70" />
            ))}
            <div className={cn(bar, 'ml-auto h-2.5 w-6')} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function SkeletonSectionLabel({ width = 56 }: { width?: number }) {
  return <div className={cn(bar, 'mt-4 mb-1 h-2.5 bg-muted/60')} style={{ width }} />
}

// Mirrors TodayProgressCard: ring, 2×2 stats and the week's bars.
export function SkeletonProgressCard() {
  return (
    <div className="mt-3 rounded-2xl border border-border bg-card/60 px-3.5 pt-3 pb-2.5">
      <div className="flex items-center gap-4">
        <div className="size-[76px] shrink-0 animate-pulse rounded-full border-[7px] border-muted" />
        <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i}>
              <div className="flex h-6 items-center">
                <div className={cn(bar, 'h-4 w-10')} />
              </div>
              <div className="flex h-4 items-center">
                <div className={cn(bar, 'h-2.5 w-16 bg-muted/60')} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex h-8 items-end gap-1">
        {[60, 85, 30, 0, 0, 0, 0].map((h, i) => (
          <div
            key={i}
            className="flex-1 animate-pulse rounded-[3px] bg-muted"
            style={{ height: `${Math.max(12, h)}%` }}
          />
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="flex flex-1 justify-center">
            <div className="h-2 w-2 animate-pulse rounded-sm bg-muted/60" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function SkeletonGoalCard({ title = 60 }: { title?: number }) {
  return (
    <div className="flex gap-3 rounded-xl border border-border/60 bg-card p-3">
      <div className="size-12 shrink-0 animate-pulse rounded-[10px] bg-muted" />
      <div className="min-w-0 flex-1">
        <div className="flex h-[21px] items-center">
          <div className={cn(bar, 'h-3.5')} style={{ width: `${title}%` }} />
        </div>
        <div className="mt-1 flex h-4 items-center gap-2.5">
          <div className={cn(bar, 'h-2.5 w-16 bg-muted/60')} />
          <div className={cn(bar, 'h-2.5 w-10 bg-muted/60')} />
        </div>
        <div className="mt-1.5 flex h-4 items-center gap-2.5">
          <div className={cn(bar, 'h-2.5 w-24 bg-muted/60')} />
          <div className="h-[3px] min-w-10 flex-1 rounded-full bg-muted sm:max-w-28" />
          <div className={cn(bar, 'h-2.5 w-7 bg-muted/60')} />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="size-4 shrink-0 animate-pulse rounded-full bg-muted" />
          <div className={cn(bar, 'h-3')} style={{ width: `${title + 10}%` }} />
        </div>
      </div>
    </div>
  )
}

function SkeletonPlanTask({ title }: { title: number }) {
  return (
    <div className="flex items-center gap-3 border-b border-border/30 py-1.5 last:border-b-0">
      <div className="size-[18px] shrink-0 animate-pulse rounded-full bg-muted" />
      <div className="flex h-5 flex-1 items-center">
        <div className={cn(bar, 'h-3')} style={{ width: `${title}%` }} />
      </div>
    </div>
  )
}

// Mirrors GoalTree / PlanOutline: optional "Up next" card, the current
// milestone open, later milestones folded into cards.
export function SkeletonPlan({ upNext = false }: { upNext?: boolean }) {
  return (
    <div className="space-y-6">
      {upNext && (
        <div className="rounded-2xl border border-primary/25 bg-primary/5 px-4 pt-3 pb-1">
          <div className={cn(bar, 'mb-2 h-2.5 w-14 bg-primary/20')} />
          {[52, 64, 44].map((w) => (
            <SkeletonPlanTask key={w} title={w} />
          ))}
        </div>
      )}
      <div>
        <div className="grid gap-1.5">
          <div className="flex h-5 items-center justify-between">
            <div className={cn(bar, 'h-2.5 w-20 bg-muted/60')} />
            <div className={cn(bar, 'h-4 w-14 rounded-full')} />
          </div>
          <div className="flex h-[25px] items-center">
            <div className={cn(bar, 'h-4 w-3/5')} />
          </div>
          <div className="flex h-4 items-center gap-2.5">
            <div className="h-1 flex-1 rounded-full bg-muted" />
            <div className={cn(bar, 'h-2.5 w-8 bg-muted/60')} />
          </div>
        </div>
        <div className="mt-1">
          {[48, 62, 40, 56].map((w) => (
            <SkeletonPlanTask key={w} title={w} />
          ))}
        </div>
      </div>
      {[46, 58].map((w) => (
        <div key={w} className="grid gap-1.5 rounded-xl border border-border/60 px-4 py-3">
          <div className="flex h-5 items-center justify-between">
            <div className={cn(bar, 'h-2.5 w-20 bg-muted/60')} />
            <div className={cn(bar, 'h-4 w-14 rounded-full bg-muted/60')} />
          </div>
          <div className="flex h-[22px] items-center">
            <div className={cn(bar, 'h-3.5')} style={{ width: `${w}%` }} />
          </div>
          <div className={cn(bar, 'h-2.5 w-24 bg-muted/60')} />
        </div>
      ))}
    </div>
  )
}
